import {
  CategoryLeaderboardConstructRequestObject,
  CategoryLeaderboardParam,
  CategoryLeaderboardPgPage,
  CategoryLeaderboardReq,
} from '@models/CategoryLeaderboard';
import axios from 'axios';
import { environment } from '@environments/environment';
import { getPeriodMetaFactory } from '@utils/PeriodUtils';
import { applyParamsBasedOnFilterPOST } from './utils/OverviewServiceUtils';
import { ProductDataType } from '@pages/product-analyzer/Product';
import { getCollectionsLiveDataParams } from '@utils/collectionUtils/CollectionUtils';
import { throwApiError } from '@services/utils/ErrorHandlers';

export const getCategoryLeaderboard = async (
  param: CategoryLeaderboardParam,
  controller: AbortController,
): Promise<CategoryLeaderboardPgPage> => {
  let params: any;

  try {
    const sortKey = param.sortOption.label?.toUpperCase();

    const requestObject: CategoryLeaderboardConstructRequestObject = {
      clientId: param.clientId,
      productType: param.productType,
      countryIds: param.countries.map((country) => country.id),
      productGroup: param.productGroup,
      period: param.period,
      comparisonPeriod: param.comparisonPeriod,
      periodType: param.periodType,
      shareType: param.shareType,
      groups: param.groups,
      isUsingCollectionLiveData: param.isUsingCollectionLiveData,
      showPerformance: param.showPerformance,
    };

    params = constructRequest(requestObject);

    applyParamsBasedOnFilterPOST(params, param.appliedFilters);

    const { data } = await axios.post<CategoryLeaderboardPgPage>(
      `${environment.baseUrl}/v2/market-performance/leaderboard?page=${param.pageRequest.page}&size=${
        param.pageRequest.size
      }&sortBy=${param.sortOption.headerKey}&sortDirection=${
        param.sortOption.sortDir
      }&sortKey=${sortKey}&growthType=${param.growthType.toUpperCase()}&clientId=${param.clientId}&country=${
        param.countries[0].id
      }&periodType=${param.periodType.toUpperCase()}`,
      params,
      {
        signal: controller.signal,
        params: {},
      },
    );
    return data;
  } catch (err: unknown) {
    // Delegated error handling; helper always throws. Added explicit throw for Sonar compliance.
    throwApiError(err, { excludedBrand: params?.excludedBrand }); // NOSONAR
  }
};

const constructRequest = (param: CategoryLeaderboardConstructRequestObject): CategoryLeaderboardReq => {
  const req = {} as CategoryLeaderboardReq;

  const { periodType, comparisonPeriod, isUsingCollectionLiveData = false } = param;
  const periodMetaForCL = getPeriodMetaFactory(param.period, comparisonPeriod, periodType);

  req.clientId = param.clientId;
  req.country = param.countryIds;

  if (param.productGroup && param.productGroup.length !== 0) {
    req.productGroup = param.productGroup.map((option) => {
      return option.value;
    });
  }

  req.productType = param.productType;
  req.productDataType = ProductDataType.PerformanceAnalyzer;
  if (param.groups && param.groups.length !== 0) {
    req.groups = param.groups;
  }

  req.periodType = periodType.toUpperCase().replace(/\s/g, '');
  req.period = periodMetaForCL ? periodMetaForCL.currentPeriod.toUpperCase() : '';
  req.comparisonPeriod = periodMetaForCL ? periodMetaForCL.comparisonPeriod.toUpperCase() : '';
  req.shareType = param.shareType;
  req.showPerformance = param.showPerformance;

  const collectionsParam = getCollectionsLiveDataParams(isUsingCollectionLiveData, periodType, comparisonPeriod);

  return {
    ...req,
    ...collectionsParam,
  };
};
